/**
 * MobilitySection
 *
 * Renders mobility-related information: citizenship, passport,
 * household configuration and work-model preference. Read-only when
 * `onEdit` is omitted. Countries of interest are not shown (F45 · D-27): the
 * question was removed and a stored answer is kept but never displayed.
 */

import type { ReactNode } from 'react';
import { useCountryName, useTranslation } from '@tonnie/locale';
import { Globe } from '@tonnie/assets';
import { Badge, CountryFlag } from '@tonnie/ui';
import { ProfileSectionCard } from '../ProfileSectionCard';
import type { TFunction } from 'i18next';
import { IOnboardingData, ProfileReviewEditHandler, type ProfileViewer } from '../types';

interface IMobilitySectionProps {
  /** Full onboarding data so we can show mobility + familyConfig + workModelPreference. */
  data: IOnboardingData;
  viewer: ProfileViewer;
  onEdit?: ProfileReviewEditHandler;
}

export function MobilitySection({ data, viewer, onEdit }: IMobilitySectionProps) {
  const { t } = useTranslation();
  const countryName = useCountryName();
  const mobility = data.mobility;
  const citizenships = data.citizenships ?? [];
  const notProvided = t('profile.notProvided');

  const hasAny =
    citizenships.length > 0 ||
    mobility?.hasValidPassport !== undefined ||
    data.familyConfig != null ||
    (data.workModelPreference?.length ?? 0) > 0;

  if (!hasAny) {
    return null;
  }

  const items: Array<{ label: string; value: ReactNode }> = [
    {
      label: t('onboarding.mobility.citizenship'),
      value:
        citizenships.length > 0 ? (
          <div className="flex flex-wrap gap-1 justify-end" data-testid="profile-citizenships">
            {citizenships.map((code) => (
              <Badge key={code} variant="secondary" className="text-xs">
                <CountryFlag code={code} size="xs" className="mr-1.5" />
                {countryName(code)}
              </Badge>
            ))}
          </div>
        ) : (
          notProvided
        ),
    },
    {
      // The citizen's own question is asked of them; a company reads a fact.
      label:
        viewer === 'self'
          ? t('onboarding.mobility.validPassport')
          : t('profile.review.company.validPassport'),
      value:
        mobility?.hasValidPassport === true
          ? t('common.yes')
          : mobility?.hasValidPassport === false
            ? t('common.no')
            : notProvided,
    },
    {
      label: t('onboarding.family.householdInfo'),
      value: (() => {
        const family = data.familyConfig;
        // The citizen answered in the first person ("I have a partner/spouse");
        // a company reads the facts, as chips.
        const parts =
          viewer === 'self'
            ? [
                family?.hasPartner && t('onboarding.family.hasPartner'),
                family?.hasChildren &&
                  `${t('onboarding.family.hasChildren')} (${family.childrenCount ?? 0})`,
                family?.hasPets && t('onboarding.family.hasPets'),
              ]
            : [
                family?.hasPartner && t('profile.review.company.partner'),
                family?.hasChildren &&
                  t('profile.review.company.children', { count: family.childrenCount ?? 0 }),
                family?.hasPets && t('profile.review.company.pets'),
              ];
        const said = parts.filter((part): part is string => typeof part === 'string');
        if (said.length === 0) return notProvided;
        if (viewer === 'self') return said.join(', ');
        return (
          <div className="flex flex-wrap gap-1 justify-end" data-testid="profile-household">
            {said.map((part) => (
              <Badge key={part} variant="secondary" className="text-xs">
                {part}
              </Badge>
            ))}
          </div>
        );
      })(),
    },
    {
      label: t('onboarding.personalInfo.workModelPreference'),
      value:
        data.workModelPreference && data.workModelPreference.length > 0 ? (
          <div className="flex flex-wrap gap-1 justify-end" data-testid="profile-work-models">
            {data.workModelPreference.map((m) => (
              <Badge key={m} variant="outline" className="text-xs">
                {workModelLabel(t, m)}
              </Badge>
            ))}
          </div>
        ) : (
          notProvided
        ),
    },
  ];

  return (
    <ProfileSectionCard
      icon={Globe}
      title={t('profile.mobility')}
      description={t('profile.mobilityDescription')}
      stepId="mobility"
      onEdit={onEdit}
    >
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex justify-between items-start gap-4 text-sm">
            <span className="text-muted-foreground shrink-0">{item.label}</span>
            <span className="text-right font-medium">{item.value}</span>
          </div>
        ))}
      </div>
    </ProfileSectionCard>
  );
}

/** A stored work model in the reader's language — never the stored slug ("on-site"). */
function workModelLabel(t: TFunction, model: string): string {
  switch (model) {
    case 'remote':
      return t('onboarding.personalInfo.workModel.remote');
    case 'hybrid':
      return t('onboarding.personalInfo.workModel.hybrid');
    case 'on-site':
      return t('onboarding.personalInfo.workModel.on-site');
    case 'flexible':
      return t('onboarding.personalInfo.workModel.flexible');
    default:
      return model;
  }
}
